import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as systemMasterAction from '../../SystemMasters/Store/SystemMasters.action';
import * as colourAction from '../../Library/ColorLibary/store/ColorLibrary.action';
import {
  createOptions,
  checkArray,
  converDropdownDataFilterData,
  getMatchingCustomAttributeIds,
  getMatchingMultiCustomAttributes,
  getCurrentYearLabel,
} from '../../../Components/helper';
import { useProductInformationState } from './Provider';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { FilterIcon, ClearIcon, SaveIcon } from '../../../bombiix-icon';
import { NewSelectMultiSelect } from '../../../Components/PIM/common/NewSelectMultiSelect';
import * as commonAction from '../../Dashboard/TodoList/TodoCommon.action';

function ProductFilter({ applyFilter, activeList }) {
  const reducer = useSelector((reducerState) => reducerState);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { UserDetails, SystemMastersDetails, BomLibraryDetails, Measuremets, ColorLibraryDetails } = reducer;
  const { userInfo, isSupplierLoggedIn, isCompanyUserLoggedIn } = UserDetails;
  const { adminUsers, companyUsers } = Measuremets;
  const { ReferenceSupplier } = BomLibraryDetails;
  const { ColorWaysLibList, ColorLibGroup: colorLibList } = ColorLibraryDetails;
  const {
    brandList,
    category1List,
    category2List,
    pcTypeList,
    productStatusList,
    filterSeasonList,
    category3List,
    category4List,
    customAttributeList,
    pcpCustomAttribute,
    productIdentifierList,
    identifierType,
    licensorList,
    licenseList,
    likenessList,
    subBrandList,
    subBrandAccess,
  } = SystemMastersDetails;
  const [isApply, setIsApply] = useState(() => {
    const stored = localStorage.getItem('productListFilter');
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      return Object.values(parsed).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
    } catch {
      return false;
    }
  });
  const { filterState, setFilterState } = useProductInformationState();
  const [isSubBrandAccess, setSubBrandAccess] = useState(false);
  const [userSuppliers, setUserSuppliers] = useState([]);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [customAttributeMastersReady, setCustomAttributeMastersReady] = useState(false);
  const {
    monthOptions,
    yearOptions,
    activeBrand,
    activeProductType,
    activeCat1,
    activeCat2,
    activeCat3,
    activeCat4,
    activeSupplier,
    activeSeason,
    activeMonth,
    activeYear,
    activeStatus,
    activeCreatedBy,
    dynamicTypes,
    textCustomAttribute,
    productIdentifiers,
    activeLicensor,
    activeLicense,
    activeLikeness,
    activeSubBrand,
    activeColorway,
    activeColorGroup,
  } = filterState;

  // update month options when year is selected or changed
  const monthYearBasedOptions = useMemo(() => {
    const months = monthOptions;
    const years = (activeYear || []).slice().sort((a, b) => a - b); // sort years ascending

    if (months.length > 0 && years.length > 0) {
      const combined = [];

      // Loop through months first, then years in ascending order
      months.forEach((month) => {
        years.forEach((year) => {
          combined.push({
            ...month,
            _id: `${month.value}_${year}`,
            name: `${month.value}_${year}`,
            label: `${month.label} ${year}`,
            value: `${month.value}_${year}`,
            year,
          });
        });
      });

      return combined;
    }

    return months;
  }, [activeYear, monthOptions]);

  const handleFilterApply = () => {
    const filterObj = {
      brandId: activeBrand,
      productType: activeProductType,
      catOneId: activeCat1,
      catTwoId: activeCat2,
      catThreeId: activeCat3,
      catFourId: activeCat4,
      supplierId: activeSupplier,
      seasonId: activeSeason,
      month: activeMonth,
      year: activeYear.map((vl) => vl.toString()),
      status: activeStatus,
      createdBy: activeCreatedBy,
      dynamicTypes,
      textCustomAttribute,
      productIdentifiers,
      licensorId: activeLicensor,
      licenseId: activeLicense,
      likenessId: activeLikeness,
      subBrandId: activeSubBrand,
      colorwayId: activeColorway,
      colorGroupId: activeColorGroup,
    };
    localStorage.setItem('productListFilter', JSON.stringify(filterObj));
    setIsApply(Object.values(filterObj).some((v) => (Array.isArray(v) ? v.length > 0 : !!v)));
    applyFilter(filterObj);
  };

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      await dispatch(systemMasterAction.getSubBrandAccess());
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  /**
   * Load both custom attribute masters the filter panel depends on and flag when they have
   * settled. The readiness flag gates the dynamicTypes reconciliation below so a persisted
   * filter is never discarded while the masters are still in flight.
   */
  useEffect(() => {
    const fetchCustomAttributeMasters = async () => {
      await Promise.all([
        dispatch(systemMasterAction.getCustomAttributeFreeText()),
        dispatch(systemMasterAction.getMultiCustomAttribute({ isActive: true })),
      ]);
      setCustomAttributeMastersReady(true);
    };
    fetchCustomAttributeMasters();
  }, [dispatch]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    const fetchColorways = async () => {
      await dispatch(
        colourAction.fetchColorListLibrary({
          loggedInUserId: userInfo?.userId,
        }),
      );
    };
    if (userInfo?.userId) {
      fetchColorways();
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    const fetchColorGroups = async () => {
      if (userInfo?.userId) {
        await dispatch(
          colourAction.fetchColorGroupLibrary({
            loggedInUserId: userInfo.userId,
            isDetails: true,
          }),
        );
      }
    };
    fetchColorGroups();
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    if (subBrandAccess) {
      setSubBrandAccess(subBrandAccess);
    }
  }, [subBrandAccess]);

  useEffect(() => {
    if (checkArray(activeCat2) && checkArray(activeCat1) && userInfo?.userId) {
      dispatch(
        systemMasterAction.getCategory3Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryType: 3,
          loggedInUserId: userInfo?.userId,
        }),
      );
    }
  }, [activeCat2, activeCat1, dispatch, userInfo?.userId]);

  useEffect(() => {
    if (checkArray(activeCat2) && checkArray(activeCat1) && userInfo?.userId && checkArray(activeCat3)) {
      dispatch(
        systemMasterAction.getCategory4Details({
          categoryOneId: activeCat1,
          categoryTwoId: activeCat2,
          categoryThreeId: activeCat3,
          categoryType: 4,
          loggedInUserId: userInfo?.userId,
        }),
      );
    }
  }, [activeCat2, activeCat1, activeCat3, dispatch, userInfo?.userId]);

  const handleFilterLicense = (val, key) => {
    setFilterState((prevState) => {
      const newState = { ...prevState, [key]: val };

      if (key === 'activeLicensor') {
        newState.activeLicense = [];
        newState.activeLikeness = [];

        if (Array?.isArray(val) && val?.length > 0) {
          dispatch(
            systemMasterAction.getLicenseDetails({
              licensorIds: val,
              loggedInUserId: userInfo?.userId,
              isActive: true,
            }),
          );
        }
        dispatch(systemMasterAction.clearLikenessData());
        dispatch(systemMasterAction.clearLicenseData());
      }

      if (key === 'activeLicense') {
        newState.activeLikeness = [];

        if (Array?.isArray(val) && val?.length > 0 && checkArray(filterState?.activeLicensor)) {
          dispatch(
            systemMasterAction.getLikenessDetails({
              licensorIds: filterState?.activeLicensor,
              licenseIds: val,
              loggedInUserId: userInfo?.userId,
              isActive: true,
            }),
          );
        }
        dispatch(systemMasterAction.clearLikenessData());
      }

      return newState;
    });
  };

  const handleFilterSelect = (val, key) => {
    if (['activeCat2', 'activeCat1'].includes(key)) {
      setFilterState((prevState) => ({
        ...prevState,
        ...(catBasedOnPTData && key === 'activeCat1' ? { activeCat2: [] } : {}),
        activeCat3: [],
        activeCat4: [],
      }));
    }
    if (catBasedOnPTData && key === 'activeProductType') {
      setFilterState((prevState) => ({
        ...prevState,
        activeCat1: [],
        activeCat2: [],
        activeCat3: [],
        activeCat4: [],
      }));
    }

    if (['activeYear'].includes(key)) {
      const remainingYears = val || [];
      // filter activeMonth to keep only months that match remaining years
      const validMonths = (activeMonth || []).filter((monthStr) => {
        const match = monthStr.match(/^([a-zA-Z]+)_(\d{4})$/);
        if (!match) return true; // keep if no year part
        const year = Number(match[1]);
        return remainingYears.includes(year);
      });
      setFilterState((prevState) => ({
        ...prevState,
        activeMonth: validMonths,
      }));
      //  obj.activeMonth = null;
    }
    if (key === 'activeBrand') {
      dispatch(
        systemMasterAction.getSubBrandDetails({
          brandId: val,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );

      setFilterState((prevState) => {
        const validSubBrands =
          prevState.activeSubBrand?.filter((subBrandId) => {
            const subBrand = subBrandList?.find((sb) => sb._id === subBrandId);
            return subBrand && val?.includes(subBrand.brandId);
          }) || [];

        return {
          ...prevState,
          activeSubBrand: validSubBrands,
        };
      });
    }

    setFilterState((prevState) => ({
      ...prevState,
      [key]: val,
    }));
  };

  const handleFilterDynamicSelect = (items, colId, mode) => {
    const copyState = cloneDeep(dynamicTypes).filter((e) => e.colId !== colId);

    if (mode === 'date-range') {
      const range = items || {};
      const hasValue = range.from !== null && range.from !== undefined || range.to !== null && range.to !== undefined;
      if (hasValue) {
        copyState.push({ colId, from: range.from || null, to: range.to || null, mode: 'date-range' });
      }
      setFilterState((prevState) => ({ ...prevState, dynamicTypes: copyState }));
      return;
    }

    const obj =
      checkArray(items) &&
      items.map((valId) => {
        return { colId, valId };
      });

    setFilterState((prevState) => ({
      ...prevState,
      dynamicTypes: obj ? [...copyState, ...obj] : [...copyState],
    }));
  };

  useEffect(() => {
    const localstorageFilter = JSON.parse(localStorage.getItem('productListFilter'));
    if (localstorageFilter) {
      for (const keys in localstorageFilter) {
        if (localstorageFilter[keys]?.length > 0) {
          setIsApply(true);
        }
      }
      if (localstorageFilter?.brandId?.length > 0) {
        dispatch(
          systemMasterAction.getSubBrandDetails({
            brandId: localstorageFilter?.brandId,
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      }

      setFilterState((prevState) => ({
        ...prevState,
        activeBrand: localstorageFilter?.brandId || [],
        activeProductType: localstorageFilter?.productType || [],
        activeCat1: localstorageFilter?.catOneId || [],
        activeCat2: localstorageFilter?.catTwoId || [],
        activeCat3: localstorageFilter?.catThreeId || [],
        activeCat4: localstorageFilter?.catFourId || [],
        activeSupplier: localstorageFilter?.supplierId || [],
        activeSeason: localstorageFilter?.seasonId || [],
        activeMonth: localstorageFilter?.month || [],
        activeYear: localstorageFilter?.year || [],
        activeStatus: localstorageFilter?.status || [],
        activeCreatedBy: localstorageFilter?.createdBy || [],
        textCustomAttribute: localstorageFilter?.textCustomAttribute || [],
        productIdentifiers: localstorageFilter?.productIdentifiers || [],
        dynamicTypes: localstorageFilter?.dynamicTypes || [],
        activeLicensor: [],
        activeLicense: [],
        activeLikeness: [],
        activeSubBrand: localstorageFilter?.subBrandId || [],
      }));
      dispatch(
        systemMasterAction.getLicensorDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );

      if (localstorageFilter?.licensorId?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: localstorageFilter?.licensorId,
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      }

      if (localstorageFilter?.licensorId?.length > 0 && localstorageFilter?.licenseId?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: localstorageFilter?.licensorId,
            licenseIds: localstorageFilter?.licenseId,
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      }
      const delayRestore = setTimeout(() => {
        setFilterState((prevState) => ({
          ...prevState,
          activeLicensor: localstorageFilter?.licensorId,
          activeLicense: localstorageFilter?.licenseId,
          activeLikeness: localstorageFilter?.likenessId,
          activeColorway: localstorageFilter?.colorwayId || [],
          activeColorGroup: localstorageFilter?.colorGroupId || [],
        }));
      }, 300);

      return () => clearTimeout(delayRestore);
    }
    dispatch(
      systemMasterAction.getLicensorDetails({
        loggedInUserId: userInfo?.userId,
        isActive: true,
      }),
    );
  }, [dispatch, userInfo?.userId]);

  const handleFilterClear = () => {
    setIsApply(false);
    const filterObj = {
      activeBrand: [],
      activeProductType: [],
      activeCat1: [],
      activeCat2: [],
      activeCat3: [],
      activeCat4: [],
      activeSupplier: [],
      activeSeason: [],
      activeMonth: [],
      activeYear: [],
      activeStatus: [],
      activeCreatedBy: [],
      textCustomAttribute: [],
      productIdentifiers: [],
      dynamicTypes: [],
      activeLicensor: [],
      activeLicense: [],
      activeLikeness: [],
      activeSubBrand: [],
      activeColorway: [],
      activeColorGroup: [],
    };
    localStorage.removeItem('productListFilter');
    setIsApply(false);
    setFilterState((prevState) => ({
      ...prevState,
      ...filterObj,
    }));
    applyFilter(filterObj);
  };

  useEffect(() => {
    const callFn = async () => {
      if (isCompanyUserLoggedIn) {
        const data = await dispatch(commonAction.getUserSuppliers());
        const supplist = data?.suppliers?.length
          ? data?.suppliers?.map((ele) => {
              if (ele.companies.length) {
                ele.name = ele.companies[0].supplierName;
              }
              return { _id: ele._id, name: ele.name };
            })
          : [];
        setUserSuppliers(supplist);
      }
    };
    callFn();
  }, [isCompanyUserLoggedIn, dispatch]);

  const brandOptions = useMemo(() => {
    if (checkArray(brandList)) return createOptions(brandList, 'name', '_id');
    return [];
  }, [brandList]);

  const subBrandOptions = useMemo(() => {
    if (checkArray(subBrandList)) return createOptions(subBrandList, 'name', '_id');
    return [];
  }, [subBrandList]);

  const category1Options = useMemo(() => {
    if (!checkArray(category1List)) return [];
    if (!catBasedOnPTData) return createOptions(category1List, 'name', '_id');

    const ptIds = Array.isArray(activeProductType) ? activeProductType : [];
    if (!ptIds?.length) return createOptions(category1List, 'name', '_id');

    const filtered = (category1List || []).filter((c1) => {
      const rel = c1?.productType || [];
      const arr = Array.isArray(rel) ? rel : [rel];
      return arr?.some((id) => ptIds.includes(id?._id || id));
    });
    return createOptions(filtered, 'name', '_id');
  }, [category1List, catBasedOnPTData, activeProductType]);

  const category2Options = useMemo(() => {
    if (!checkArray(category2List)) return [];
    if (!catBasedOnPTData) return createOptions(category2List, 'name', '_id');

    const cat1Sel = Array.isArray(activeCat1) ? activeCat1 : [];
    if (!cat1Sel?.length) return createOptions(category2List, 'name', '_id');

    const filtered = (category2List || []).filter((c2) => {
      const c1Rel = c2?.categoryOneId || c2?.catOneIds || [];
      const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
      return a1?.some((id) => cat1Sel.includes(id?._id || id));
    });
    return createOptions(filtered, 'name', '_id');
  }, [category2List, catBasedOnPTData, activeCat1]);

  const productTypeOptions = useMemo(() => {
    if (checkArray(pcTypeList)) return createOptions(pcTypeList, 'name', '_id');
    return [];
  }, [pcTypeList]);

  const productStatusOptions = useMemo(() => {
    if (checkArray(productStatusList)) return createOptions(productStatusList, 'name', '_id');
    return [];
  }, [productStatusList]);

  const seasonOptions = useMemo(() => {
    if (checkArray(filterSeasonList)) return createOptions(filterSeasonList, 'name', '_id');
    return [];
  }, [filterSeasonList]);

  const supplierOptions = useMemo(() => {
    let options = [];

    if (checkArray(ReferenceSupplier) && !isCompanyUserLoggedIn) {
      options = createOptions(ReferenceSupplier, 'name', '_id');
    } else if (checkArray(userSuppliers) && isCompanyUserLoggedIn) {
      options = createOptions(userSuppliers, 'name', '_id');
    }

    // Sort alphabetically by label (case-insensitive)
    return options?.sort((a, b) => a?.label?.trim()?.localeCompare(b?.label?.trim(), undefined, { sensitivity: 'base' }));
  }, [ReferenceSupplier, isCompanyUserLoggedIn, userSuppliers]);

  const dynamicFields = useMemo(() => {
    if (checkArray(customAttributeList)) return converDropdownDataFilterData(customAttributeList);
    return [];
  }, [customAttributeList]);

  const multiDropdownFields = useMemo(() => {
    if (checkArray(pcpCustomAttribute)) return pcpCustomAttribute.filter((attribute) => attribute?.isActive !== false);
    return [];
  }, [pcpCustomAttribute]);

  const category3Options = useMemo(() => {
    if (!checkArray(category3List)) return [];
    if (!catBasedOnPTData) return createOptions(category3List, 'name', '_id');

    const cat1Sel = Array.isArray(activeCat1) ? activeCat1 : [];
    const cat2Sel = Array.isArray(activeCat2) ? activeCat2 : [];
    if (!cat1Sel?.length || !cat2Sel?.length) return [];

    const filtered = (category3List || []).filter((c3) => {
      const c1Rel = c3?.categoryOneId || c3?.catOneIds || [];
      const c2Rel = c3?.categoryTwoId || c3?.catTwoIds || [];
      const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
      const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
      return a1?.some((id) => cat1Sel.includes(id?._id || id)) && a2?.some((id) => cat2Sel.includes(id?._id || id));
    });
    return createOptions(filtered, 'name', '_id');
  }, [category3List, catBasedOnPTData, activeCat1, activeCat2]);

  const category4Options = useMemo(() => {
    if (!checkArray(category4List)) return [];
    if (!catBasedOnPTData) return createOptions(category4List, 'name', '_id');

    const cat1Sel = Array.isArray(activeCat1) ? activeCat1 : [];
    const cat2Sel = Array.isArray(activeCat2) ? activeCat2 : [];
    const cat3Sel = Array.isArray(activeCat3) ? activeCat3 : [];
    if (!cat1Sel?.length || !cat2Sel?.length || !cat3Sel?.length) return [];

    const filtered = (category4List || []).filter((c4) => {
      const c1Rel = c4?.categoryOneId || c4?.catOneIds || [];
      const c2Rel = c4?.categoryTwoId || c4?.catTwoIds || [];
      const c3Rel = c4?.categoryThreeId || c4?.catThreeIds || [];
      const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
      const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
      const a3 = Array.isArray(c3Rel) ? c3Rel : [c3Rel];
      const matchC1 = a1?.some((id) => cat1Sel.includes(id?._id || id));
      const matchC2 = a2?.some((id) => cat2Sel.includes(id?._id || id));
      const matchC3 = a3?.some((id) => cat3Sel.includes(id?._id || id));
      return matchC1 && matchC2 && matchC3;
    });
    return createOptions(filtered, 'name', '_id');
  }, [category4List, catBasedOnPTData, activeCat1, activeCat2, activeCat3]);

  const licensorOptions = useMemo(() => {
    if (checkArray(licensorList)) return createOptions(licensorList, 'name', '_id');
    return [];
  }, [licensorList]);

  const licenseOptions = useMemo(() => {
    if (checkArray(licenseList)) return createOptions(licenseList, 'name', '_id');
    return [];
  }, [licenseList]);

  const likenessOptions = useMemo(() => {
    if (checkArray(likenessList)) return createOptions(likenessList, 'name', '_id');
    return [];
  }, [likenessList]);

  const colorwayOptions = useMemo(() => {
    if (checkArray(ColorWaysLibList)) return createOptions(ColorWaysLibList, 'refName', '_id');
    return [];
  }, [ColorWaysLibList]);

  const colorGroupOptions = useMemo(() => {
    if (checkArray(colorLibList)) {
      return createOptions(colorLibList, 'name', '_id');
    }
    return [];
  }, [colorLibList]);

  const createdByOption = useMemo(() => {
    const allUser = [adminUsers, ...companyUsers];
    if (checkArray(allUser)) {
      return allUser?.map((ele) => {
        return {
          label: `${ele?.firstName} ${ele?.lastName}`,
          value: ele?._id,
        };
      });
    }
    return [];
  }, [adminUsers, companyUsers]);

  const TextCustomAttributeOptions = useMemo(() => {
    let options = [];
    if (checkArray(customAttributeList)) {
      options = customAttributeList?.filter((ele) => ele?.type === 'text' && ele?.isLable === false)?.map((e) => ({ label: e.keyName, value: e._id }));
    }
    return options;
  }, [customAttributeList]);

  const DatePickerCustomAttributeOptions = useMemo(() => {
    if (!checkArray(customAttributeList)) return [];
    return customAttributeList
      .filter((ele) => ele?.type === 'date-picker' && ele?.isLable === true)
      .map((e) => ({ label: e.keyName, value: e._id, datePickerConfig: e.datePickerConfig || {} }));
  }, [customAttributeList]);

  const RadioButtonCustomAttributeOptions = useMemo(() => {
    if (!checkArray(customAttributeList)) return [];
    const parents = customAttributeList.filter((ele) => ele?.type === 'radio-button' && ele?.isLable === true);
    return parents.map((p) => ({
      label: p.keyName,
      value: p._id,
      values: customAttributeList
        .filter((c) => c?.type === 'radio-button' && c?.isLable === false && c?.lableId === p._id && c?.isActive)
        .map((c) => ({ label: c.keyName, value: c._id })),
    }));
  }, [customAttributeList]);

  const ProductIdentifierOptions = useMemo(() => {
    let options = [];
    if (checkArray(productIdentifierList)) {
      options = productIdentifierList
        ?.filter((ele) => (identifierType ? !['Product number', 'Colourway', 'Size', 'Length'].includes(ele?.name) : ele?.name !== 'Product number'))
        ?.map((e) => ({ label: e?.name, value: e?._id }));
    }
    return options;
  }, [productIdentifierList, identifierType]);

  const selectedBrandIds = useMemo(() => (Array.isArray(activeBrand) ? activeBrand : [activeBrand]).filter(Boolean), [activeBrand]);

  const selectedProductTypeIds = useMemo(
    () => (Array.isArray(activeProductType) ? activeProductType : [activeProductType]).filter(Boolean),
    [activeProductType],
  );

  /**
   * Ids of the dropdown / radio custom attributes configured in System Masters for BOTH the
   * selected Brand and the selected Product Type. Empty whenever either selection is missing,
   * so no dropdown custom attribute filter is offered until the user has narrowed by both.
   */
  const brandAndTypeMatchedFieldIds = useMemo(
    () => getMatchingCustomAttributeIds(dynamicFields, selectedBrandIds, selectedProductTypeIds),
    [dynamicFields, selectedBrandIds, selectedProductTypeIds],
  );

  /**
   * Dropdown custom attributes are dropped from the panel entirely when they do not match the
   * current Brand + Product Type selection. Radio-button and date-picker attributes keep their
   * existing enable / disable behaviour.
   */
  const renderableDynamicFields = useMemo(
    () => dynamicFields.filter((attribute) => (attribute?.type === 'dropdown' ? brandAndTypeMatchedFieldIds.includes(attribute._id) : true)),
    [dynamicFields, brandAndTypeMatchedFieldIds],
  );

  /**
   * Multi dropdown custom attributes are configured by Product Type only in System Masters, so
   * they are offered as soon as a Product Type is selected and ignore the Brand selection.
   */
  const visibleMultiDropdownFields = useMemo(
    () => getMatchingMultiCustomAttributes(multiDropdownFields, selectedProductTypeIds),
    [multiDropdownFields, selectedProductTypeIds],
  );

  /**
   * Drop any applied custom attribute value whose attribute is no longer offered for the current
   * Brand / Product Type selection, so a filter the user can no longer see never constrains the
   * product list. Held back until the masters have loaded to protect a restored filter.
   */
  useEffect(() => {
    if (!customAttributeMastersReady) return;
    const validFieldIds = [...brandAndTypeMatchedFieldIds, ...visibleMultiDropdownFields.map((attribute) => attribute._id)];
    setFilterState((prevState) => ({
      ...prevState,
      dynamicTypes: prevState?.dynamicTypes?.filter((selectedValue) => validFieldIds?.includes(selectedValue?.colId)),
    }));

    try {
      const storedFilter = JSON.parse(localStorage.getItem('productListFilter'));
      if (!checkArray(storedFilter?.dynamicTypes)) return;
      const reconciled = storedFilter.dynamicTypes.filter((selectedValue) => validFieldIds.includes(selectedValue?.colId));
      if (reconciled.length !== storedFilter.dynamicTypes.length) {
        localStorage.setItem('productListFilter', JSON.stringify({ ...storedFilter, dynamicTypes: reconciled }));
      }
    } catch {
      /* A malformed persisted filter is ignored; the panel state above is already reconciled. */
    }
  }, [brandAndTypeMatchedFieldIds, visibleMultiDropdownFields, customAttributeMastersReady, setFilterState]);

  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER.FILTER')}>
      <button type="button" aria-label={t('FILTER.FILTER')} className={isApply ? 'bombiix-icon-container active' : 'bombiix-icon-container'}>
        <OverlayTrigger
          placement={t('TAKING_DATES.BUTTON')}
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={brandOptions}
                        value={brandOptions?.filter((val) => activeBrand?.includes(val?._id))}
                        placeholder={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleFilterSelect(
                            (selected || []).map((val) => val.value),
                            'activeBrand',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {isSubBrandAccess && (
                    <Col md={2}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          isClearable
                          name="group"
                          options={subBrandOptions}
                          value={subBrandOptions?.filter((val) => activeSubBrand?.includes(val?._id))}
                          placeholder="Select Sub Brand"
                          onChange={(selected) =>
                            handleFilterSelect(
                              (selected || []).map((val) => val.value),
                              'activeSubBrand',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}
                  {activeList !== 'bin' && (
                    <Col md={2}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          isClearable
                          name="group"
                          options={productTypeOptions}
                          value={productTypeOptions.filter((val) => checkArray(productTypeOptions) && activeProductType.includes(val.value))}
                          placeholder={t('FILTER.SELECT_PRODUCT_TYPE')}
                          onChange={(selected) =>
                            handleFilterSelect(
                              (selected || []).map((val) => val.value),
                              'activeProductType',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={category1Options}
                        value={category1Options.filter((val) => checkArray(category1Options) && activeCat1.includes(val.value))}
                        placeholder={t('FILTER.SELECT_CATEGORY_ONE')}
                        onChange={(selected) =>
                          handleFilterSelect(
                            (selected || []).map((val) => val.value),
                            'activeCat1',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={category2Options}
                        value={category2Options.filter((val) => checkArray(category2Options) && activeCat2.includes(val.value))}
                        placeholder={t('FILTER.SELECT_CATEGORY_TWO')}
                        onChange={(selected) =>
                          handleFilterSelect(
                            (selected || []).map((val) => val.value),
                            'activeCat2',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={category3Options}
                        value={category3Options.filter((val) => checkArray(category3Options) && activeCat3.includes(val.value))}
                        placeholder={t('FILTER.SELECT_CATEGORY_THREE')}
                        onChange={(selected) =>
                          handleFilterSelect(
                            (selected || []).map((val) => val.value),
                            'activeCat3',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        isClearable
                        name="group"
                        options={category4Options}
                        value={category4Options.filter((val) => checkArray(category4Options) && activeCat4.includes(val.value))}
                        placeholder={t('FILTER.SELECT_CATEGORY_FOUR')}
                        onChange={(selected) =>
                          handleFilterSelect(
                            (selected || []).map((val) => val.value),
                            'activeCat4',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  {!isSupplierLoggedIn && (
                    <Col md={2}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          isClearable
                          name="group"
                          options={supplierOptions}
                          value={supplierOptions?.filter((val) => checkArray(supplierOptions) && activeSupplier.includes(val.value))}
                          placeholder={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleFilterSelect(
                              (selected || []).map((val) => val.value),
                              'activeSupplier',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}

                  {activeList !== 'bin' && (
                    <>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={seasonOptions}
                            value={seasonOptions.filter((val) => checkArray(seasonOptions) && activeSeason.includes(val.value))}
                            placeholder={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'activeSeason',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={yearOptions}
                            value={yearOptions.filter((val) => checkArray(yearOptions) && activeYear.includes(val.value.toString()))}
                            placeholder={t('COSTING_TO_DO_LIST.SELECT_YEAR')}
                            scrollToLabel={getCurrentYearLabel()}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value.toString()),
                                'activeYear',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={monthYearBasedOptions}
                            value={monthYearBasedOptions.filter((val) => checkArray(monthYearBasedOptions) && activeMonth.includes(val.value))}
                            placeholder={t('PROGRESS_INDICATOR.SELECT_MONTH')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'activeMonth',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      {!isEmpty(renderableDynamicFields) &&
                        renderableDynamicFields?.map((ele) => {
                          const matchesBrand = ele?.brand?.some((brandId) => selectedBrandIds.includes(brandId));
                          const matchesProductType = ele?.productType?.some((productTypeId) => selectedProductTypeIds.includes(productTypeId));
                          let isEnabled = true;
                          if (selectedBrandIds.length > 0 && selectedProductTypeIds.length > 0) {
                            isEnabled = matchesBrand && matchesProductType;
                          }

                          let selectOpt = null;
                          if (ele.type === 'dropdown') {
                            selectOpt = createOptions(ele.values, 'keyName', '_id');
                          }
                          if (ele.type === 'dropdown' && ele.isActive) {
                            return (
                              <Col md={2} key={ele?._id}>
                                <FormGroup>
                                  <div className="custom-react-select ">
                                    <NewSelectMultiSelect
                                      isMulti
                                      isClearable
                                      name="group"
                                      options={selectOpt?.filter((val) => val.isActive === true)}
                                      value={selectOpt?.filter(
                                        (option) => checkArray(selectOpt) && dynamicTypes?.some((selectedValue) => selectedValue?.valId === option?._id),
                                      )}
                                      placeholder={`Select ${ele.keyName}`}
                                      onChange={(selected) =>
                                        handleFilterDynamicSelect(
                                          (selected || []).map((val) => val?._id),
                                          ele?._id,
                                        )
                                      }
                                    />
                                  </div>
                                </FormGroup>
                              </Col>
                            );
                          }
                          if (ele.type === 'radio-button' && ele.isActive) {
                            const radioOpts = createOptions(ele.values, 'keyName', '_id');
                            return (
                              <Col md={2} key={ele?._id}>
                                <FormGroup>
                                  <div className="custom-react-select ">
                                    <NewSelectMultiSelect
                                      isMulti
                                      isClearable
                                      name="group"
                                      options={radioOpts?.filter((val) => val.isActive === true)}
                                      isDisabled={!isEnabled}
                                      value={
                                        isEnabled ? radioOpts?.filter((val) => checkArray(radioOpts) && dynamicTypes?.some((e) => e?.valId === val?._id)) : []
                                      }
                                      placeholder={`Select ${ele.keyName}`}
                                      onChange={(selected) =>
                                        handleFilterDynamicSelect(
                                          (selected || []).map((val) => val?._id),
                                          ele?._id,
                                        )
                                      }
                                    />
                                  </div>
                                </FormGroup>
                              </Col>
                            );
                          }
                          if (ele.type === 'date-picker' && ele.isActive) {
                            const filterEntry = dynamicTypes?.find((e) => e?.colId === ele._id) || {};
                            const fromVal = filterEntry?.from ? moment(filterEntry.from, 'YYYY-MM-DD').toDate() : null;
                            const toVal = filterEntry?.to ? moment(filterEntry.to, 'YYYY-MM-DD').toDate() : null;
                            const dpCfg = ele.datePickerConfig || {};
                            return (
                              <Col md={4} key={ele?._id}>
                                <FormGroup>
                                  <label className="field-name d-block">{ele.keyName}</label>
                                  <div className="d-flex">
                                    <DatePicker
                                      selected={fromVal}
                                      onChange={(d) =>
                                        handleFilterDynamicSelect(
                                          { from: d ? moment(d).format('YYYY-MM-DD') : null, to: filterEntry?.to || null },
                                          ele?._id,
                                          'date-range',
                                        )
                                      }
                                      dateFormat={(dpCfg.dateFormat || 'DD-MMM-YYYY').replace(/Y/g, 'y').replace(/D/g, 'd')}
                                      isClearable
                                      placeholderText={t('FILTER.DATE_RANGE_FROM') || 'From'}
                                      className="form-control mr-2"
                                      disabled={!isEnabled}
                                    />
                                    <DatePicker
                                      selected={toVal}
                                      onChange={(d) =>
                                        handleFilterDynamicSelect(
                                          { from: filterEntry?.from || null, to: d ? moment(d).format('YYYY-MM-DD') : null },
                                          ele?._id,
                                          'date-range',
                                        )
                                      }
                                      dateFormat={(dpCfg.dateFormat || 'DD-MMM-YYYY').replace(/Y/g, 'y').replace(/D/g, 'd')}
                                      isClearable
                                      placeholderText={t('FILTER.DATE_RANGE_TO') || 'To'}
                                      className="form-control"
                                      minDate={fromVal || undefined}
                                      disabled={!isEnabled}
                                    />
                                  </div>
                                </FormGroup>
                              </Col>
                            );
                          }
                        })}
                      {checkArray(visibleMultiDropdownFields) &&
                        visibleMultiDropdownFields.map((attribute) => {
                          const multiSelectOpt = createOptions(attribute?.values, 'keyName', '_id');
                          return (
                            <Col md={2} key={attribute?._id}>
                              <FormGroup>
                                <div className="custom-react-select ">
                                  <NewSelectMultiSelect
                                    isMulti
                                    isClearable
                                    name="group"
                                    options={multiSelectOpt?.filter((option) => option?.isActive !== false)}
                                    value={multiSelectOpt?.filter(
                                      (option) => checkArray(multiSelectOpt) && dynamicTypes?.some((selectedValue) => selectedValue?.valId === option?._id),
                                    )}
                                    placeholder={`Select ${attribute?.keyName}`}
                                    onChange={(selected) =>
                                      handleFilterDynamicSelect(
                                        (selected || []).map((option) => option?._id),
                                        attribute?._id,
                                      )
                                    }
                                  />
                                </div>
                              </FormGroup>
                            </Col>
                          );
                        })}
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={productStatusOptions}
                            value={productStatusOptions.filter((val) => checkArray(productStatusOptions) && activeStatus.includes(val.value))}
                            placeholder={t('PROGRESS_INDICATOR.SELECT_STATUS')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'activeStatus',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={createdByOption}
                            value={createdByOption.filter((val) => checkArray(createdByOption) && activeCreatedBy.includes(val.value))}
                            placeholder={t('PROGRESS_INDICATOR.SELECTED_CREATE')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'activeCreatedBy',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="textCustomAttribute"
                            options={TextCustomAttributeOptions}
                            value={TextCustomAttributeOptions?.filter(
                              (val) => checkArray(TextCustomAttributeOptions) && textCustomAttribute?.includes(val.value),
                            )}
                            placeholder={t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_TEXT_ATTRIBUTES')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'textCustomAttribute',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="productIdentifiers"
                            options={ProductIdentifierOptions}
                            value={ProductIdentifierOptions?.filter((val) => checkArray(ProductIdentifierOptions) && productIdentifiers?.includes(val.value))}
                            placeholder={t('DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.PRODUCT_FILTER.SELECT_PRODUCT_IDENTIFIERS')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'productIdentifiers',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={licensorOptions}
                            value={licensorOptions.filter((val) => activeLicensor?.includes(val._id))}
                            placeholder="Select Licensor"
                            onChange={(selected) =>
                              handleFilterLicense(
                                (selected || []).map((val) => val._id),
                                'activeLicensor',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={licenseOptions}
                            value={licenseOptions.filter((val) => activeLicense?.includes(val._id))}
                            placeholder="Select License"
                            onChange={(selected) =>
                              handleFilterLicense(
                                (selected || []).map((val) => val._id),
                                'activeLicense',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={likenessOptions}
                            value={likenessOptions.filter((val) => Array.isArray(activeLikeness) && activeLikeness.some((id) => id === val._id))}
                            placeholder={t('LIKENESS_MASTER.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                (selected || []).map((val) => val._id),
                                'activeLikeness',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="group"
                            options={colorwayOptions}
                            value={colorwayOptions.filter((val) => activeColorway?.includes(val._id))}
                            placeholder={t('COMPONENT.PRODUCT_FILTER.FILTER.SELECT_COLOURWAY')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val._id),
                                'activeColorway',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <NewSelectMultiSelect
                            isMulti
                            isClearable
                            name="colorGroup"
                            options={colorGroupOptions}
                            value={colorGroupOptions?.filter((val) => activeColorGroup?.includes(val?._id))}
                            placeholder={t('COLOR_LIBRARY.FILTER.SELECT_COLOR_GROUP')}
                            onChange={(selected) =>
                              handleFilterSelect(
                                (selected || []).map((val) => val.value),
                                'activeColorGroup',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.CLEAR')}>
                        <button onClick={() => handleFilterClear()} aria-label="Close" className="bombiix-icon-container bombiix-icon-container--red" type="button">
                          <ClearIcon size={20} />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.APPLY')}>
                        <button onClick={() => handleFilterApply()} aria-label="Apply" className="bombiix-icon-container ml-16" type="button">
                          <SaveIcon size={20} />
                        </button>
                      </CustomTooltip>
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          <FilterIcon size={28} />
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default ProductFilter;
